import React, { useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Clock,
  Flag,
  TrendingUp
} from 'lucide-react';

function ScoreBar({ label, score, maxScore }) {
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const tone =
    pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-rose-400';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-700">{label}</span>
        <span className="font-medium text-slate-900">
          {score}/{maxScore}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AdvisoryBadge({ verdict }) {
  const styles = {
    supported: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
    weak: 'bg-rose-100 text-rose-800 ring-rose-200',
    neutral: 'bg-slate-100 text-slate-700 ring-slate-200'
  };
  const labels = {
    supported: 'Supported',
    weak: 'Weak',
    neutral: 'Neutral'
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${styles[verdict] || styles.neutral}`}>
      {labels[verdict] || verdict}
    </span>
  );
}

function formatTimelineDate(at) {
  if (!at) return '—';
  try {
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return at;
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return at;
  }
}

const transitionLabels = {
  cold_to_warm: 'Cold → Warm',
  warm_to_hot: 'Warm → Hot'
};

export default function LeadIntelligencePanel({
  intelligence,
  dimensionSignals = [],
  defaultExpanded = false
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAllTimeline, setShowAllTimeline] = useState(false);

  if (!intelligence && !dimensionSignals?.length) return null;

  const timeline = intelligence?.timeline ?? [];
  const visibleTimeline = showAllTimeline ? timeline : timeline.slice(0, 6);
  const advisories = intelligence?.stage_transition_advisories ?? [];
  const flags = intelligence?.flags ?? [];
  const intent = intelligence?.intent_analysis;

  return (
    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/80 text-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#1E4A53]">
          <TrendingUp className="h-4 w-4" />
          Lead Intelligence
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        )}
      </button>

      {expanded ? (
        <div className="space-y-3 border-t border-slate-200 px-3 pb-3 pt-3">
          {dimensionSignals?.length > 0 ? (
            <section>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-600">
                Score breakdown (100 pts)
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {dimensionSignals.map((dim) => (
                  <ScoreBar
                    key={dim.code}
                    label={dim.label}
                    score={dim.score}
                    maxScore={dim.maxScore}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {timeline.length > 0 ? (
            <section>
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-600">
                  Activity timeline
                </p>
                {intelligence?.timeline_summary ? (
                  <span className="text-[10px] text-slate-500 truncate">{intelligence.timeline_summary}</span>
                ) : null}
              </div>
              <ol className="relative space-y-0 border-l border-slate-200 pl-4">
                {visibleTimeline.map((evt, idx) => (
                  <li key={`${evt.at}-${evt.channel}-${idx}`} className="relative pb-2.5 last:pb-0">
                    <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-[#1E4A53]" />
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <Clock className="h-3 w-3 text-slate-400" />
                      <span className="text-[10px] font-medium text-slate-500">
                        {formatTimelineDate(evt.at)}
                      </span>
                      <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-700 ring-1 ring-slate-200">
                        {evt.channel}
                      </span>
                      {evt.outcome ? (
                        <span className="text-[10px] text-slate-600">{evt.outcome}</span>
                      ) : null}
                    </div>
                    {evt.preview ? (
                      <p className="mt-0.5 text-xs leading-snug text-slate-700 line-clamp-2">{evt.preview}</p>
                    ) : null}
                  </li>
                ))}
              </ol>
              {timeline.length > 6 ? (
                <button
                  type="button"
                  onClick={() => setShowAllTimeline((v) => !v)}
                  className="mt-1 text-xs font-semibold text-[#1E4A53] hover:underline">
                  {showAllTimeline ? 'Show fewer events' : `Show all ${timeline.length} events`}
                </button>
              ) : null}
            </section>
          ) : null}

          {intent?.positive_signals?.length || intelligence?.team_performance ? (
            <section className="grid gap-2 sm:grid-cols-2">
              {intent?.positive_signals?.length ? (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-800">
                    Positive signals
                  </p>
                  <ul className="mt-1.5 space-y-0.5 text-xs text-emerald-900">
                    {intent.positive_signals.map((s) => (
                      <li key={s} className="flex gap-1.5">
                        <span className="text-emerald-600 shrink-0">✓</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {intelligence?.team_performance ? (
                <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-amber-900">
                    Sales process · {intelligence.team_performance.process_score}/100
                  </p>
                  <p className="mt-1 text-xs font-medium text-amber-950 capitalize">
                    {intelligence.team_performance.quality}
                  </p>
                  {intelligence.team_performance.checks?.length ? (
                    <ul className="mt-2 space-y-1">
                      {intelligence.team_performance.checks.slice(0, 5).map((check) => (
                        <li key={check.code || check.label} className="flex items-start gap-2 text-xs text-slate-700">
                          <span className={check.status === 'done' ? 'text-emerald-600' : 'text-amber-600'}>
                            {check.status === 'done' ? '✓' : '○'}
                          </span>
                          <span>{check.label}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}

          {(intelligence?.summary_detailed || intelligence?.summary_manager) ? (
            <section className="grid gap-2 sm:grid-cols-2 text-xs text-slate-700">
              {intelligence.summary_detailed ? (
                <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">Sales view</p>
                  <p className="mt-1 leading-relaxed">{intelligence.summary_detailed}</p>
                </div>
              ) : null}
              {intelligence.summary_manager ? (
                <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">Manager view</p>
                  <p className="mt-1 leading-relaxed">{intelligence.summary_manager}</p>
                </div>
              ) : null}
            </section>
          ) : null}

          {intelligence?.manager_alert ? (
            <p className="rounded-lg bg-violet-50 px-2.5 py-2 text-xs font-medium text-violet-900">
              Manager alert — review recommended for this lead.
            </p>
          ) : null}

          {advisories.length > 0 ? (
            <section>
              <p className="mb-2 text-xs font-semibold text-slate-800">Stage move advisories</p>
              <ul className="space-y-2">
                {advisories.map((adv) => (
                  <li
                    key={`${adv.transition}-${adv.verdict}`}
                    className="rounded-lg border border-slate-200 bg-white p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-slate-900">
                        {transitionLabels[adv.transition] || adv.transition}
                      </span>
                      <AdvisoryBadge verdict={adv.verdict} />
                    </div>
                    <p className="mt-1 text-xs text-slate-600">{adv.message}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {intent?.negative_signals?.length ? (
            <section>
              <div className="rounded-lg border border-rose-100 bg-rose-50/60 p-2.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-rose-800">
                  Negative signals
                </p>
                <ul className="mt-1.5 space-y-0.5 text-xs text-rose-900">
                  {intent.negative_signals.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </section>
          ) : null}

          {flags.length > 0 ? (
            <section>
              <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-slate-800">
                <Flag className="h-3.5 w-3.5" />
                Flags
              </p>
              <ul className="space-y-1.5">
                {flags.map((flag) => (
                  <li
                    key={flag.code}
                    className={`flex items-start gap-2 rounded-lg px-2.5 py-2 text-xs ${
                      flag.severity === 'high'
                        ? 'bg-red-50 text-red-900'
                        : flag.severity === 'medium'
                          ? 'bg-amber-50 text-amber-900'
                          : 'bg-slate-100 text-slate-700'
                    }`}>
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{flag.message}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
